/**
 * Load and query gm-kb-store.json (canonical: frontend/gm-kb-store.json;
 * served at /gm-kb-store.json from public/).
 */

import { SLUG_TO_ROUTE } from './procedureRoutes';

let contentCache = null;
let contentPromise = null;

export async function loadGmContent() {
  if (contentCache) return contentCache;
  if (!contentPromise) {
    contentPromise = fetch('/gm-kb-store.json')
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load procedure content');
        return res.json();
      })
      .then((data) => {
        contentCache = data;
        return data;
      });
  }
  return contentPromise;
}

/** Pick English text from a language map or plain string. */
export function pickEn(value) {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    return value.en || value.es || Object.values(value).find((v) => typeof v === 'string') || '';
  }
  return String(value);
}

function findJurisdictionProcedure(data, slug) {
  const target = String(slug || '').trim();
  if (!target) return null;
  for (const jurisdiction of data?.jurisdictions || []) {
    for (const proc of jurisdiction?.procedures || []) {
      const cid = proc?.canonical_id || proc?.cid;
      if (cid === target) {
        return { jurisdiction, proc };
      }
    }
  }
  return null;
}

/**
 * Raw jurisdiction-schema text fields are one level deeper than the older "built card"
 * schema: ``{ text: { it, en, ... }, anchor_lang }`` instead of a flat ``{ en, es }`` map.
 * This unwraps either shape into a plain English-first string.
 */
function rawText(field) {
  if (field == null) return '';
  if (typeof field === 'string') return field;
  const bag = field.text && typeof field.text === 'object' ? field.text : field;
  if (bag == null || typeof bag !== 'object') return String(bag ?? '');
  return bag.en || bag.es || bag.it || Object.values(bag).find((v) => typeof v === 'string') || '';
}

const PROCESS_PHASE_LABELS = ['Prepare', 'Submit', 'Official review', 'Decision'];

const DEADLINE_ANCHOR_LABELS = {
  application_submitted: 'application',
  legal_entry: 'entry',
  decision_issued: 'decision',
  case_opened: 'case opening',
};

const DEADLINE_UNIT_LABELS = {
  calendar_days: 'day',
  working_days: 'working day',
  business_days: 'business day',
  months: 'month',
  years: 'year',
};

function formatDeadline(deadline) {
  if (!deadline?.offset) return null;
  const { value, unit } = deadline.offset;
  if (value == null || !unit) return null;
  const unitLabel = DEADLINE_UNIT_LABELS[unit] || String(unit).replace(/_/g, ' ').replace(/s$/, '');
  const anchor = DEADLINE_ANCHOR_LABELS[deadline.anchor_event]
    || String(deadline.anchor_event || '').replace(/_/g, ' ');
  return `deadline: ${value} ${unitLabel}(s) from ${anchor}`;
}

/**
 * Adapts a raw jurisdiction procedure entry (requirements / mechanics / outcome /
 * rights_and_limits / filings / explainers schema) into the shape ``ProcedureInfoPage``
 * expects (``card.eligibility``, ``card.process``, ``card.filings`` etc.), so procedures
 * that only exist in ``jurisdictions[]`` (not yet migrated to a built ``procedures[slug]``
 * card) still render full content instead of an empty page.
 */
function buildJurisdictionCard(jurisdiction, proc) {
  const title = rawText(proc?.label);
  const nativeTitle = proc?.label?.text?.[proc?.label?.anchor_lang] || '';
  const overview = rawText(proc?.description);

  const eligibility = (proc?.requirements || []).map((r) => ({
    id: String(r.requirement_id || '').split(':').pop() || r.requirement_id,
    label: rawText(r.description),
    source: r.source || null,
  }));

  const requiredDocuments = (proc?.required_documents_manifest || []).map((d, i) => {
    const text = rawText(d.note);
    const requirement = d.requirement || '';
    const id = (() => {
      const lower = text.toLowerCase();
      const req = String(requirement).toLowerCase();
      if (req === 'identity' || /passport/.test(lower)) return 'doc.passport';
      if (req === 'category' || /contract|engagement/.test(lower)) return 'doc.contract';
      if (/visa/.test(lower)) return 'doc.visa';
      return `doc.${String(requirement || `document_${i}`).replace(/[^a-z0-9]+/gi, '_').toLowerCase()}`;
    })();
    const titleMap = {
      category: 'Engagement contract',
      qualification: 'Professional qualification certificate',
      accommodation: 'Proof of accommodation',
      identity: 'Passport',
      health_insurance: 'Health insurance certificate',
      means: 'Proof of means (CUD / tax declaration)',
    };
    return {
      id,
      requirement,
      title: titleMap[requirement] || text.slice(0, 48) || id,
      text,
      mandatory: d.mandatory !== false,
      providedBy: 'applicant',
    };
  });

  const rightsAndLimits = (proc?.rights_and_limits || []).map((r) => ({
    id: r.id,
    text: rawText(r.text),
    source: r.source || null,
  }));

  const outcome = proc?.outcome
    ? {
      what_you_get: rawText(proc.outcome.what_you_get),
      duration: rawText(proc.outcome.duration),
      takes_effect: rawText(proc.outcome.takes_effect),
      renewal: rawText(proc.outcome.renewal),
      source: proc.outcome.source || null,
    }
    : null;

  const steps = (proc?.mechanics || [])
    .slice()
    .sort((a, b) => (a.step || 0) - (b.step || 0))
    .map((m, i) => ({
      phase: PROCESS_PHASE_LABELS[i] || `Step ${i + 1}`,
      acts: m.actor,
      what: rawText(m.description),
      deadline: formatDeadline(m.deadline),
    }));

  const filings = (proc?.filings || []).map((f) => ({
    id: f.id,
    order: f.order,
    label: rawText(f.label),
    office: rawText(f.office),
    filer: f.filer,
    contains_forms: f.contains_forms || [],
    contains_documents: f.contains_documents || [],
  }));

  const explainers = (proc?.explainers || []).map((e) => ({
    id: e.id,
    applies_to: e.applies_to,
    question: rawText(e.question),
    authoritative: (e.authoritative || []).map((a) => ({
      point: rawText(a.point),
      source: a.source || null,
    })),
    non_authoritative: (e.non_authoritative || []).map((a) => ({
      point: rawText(a.point),
      source: a.source || null,
    })),
  }));

  return {
    built: true,
    procedure: {
      canonical_id: proc?.canonical_id,
      title,
      subtitle: nativeTitle,
      overview,
      jurisdiction: jurisdiction?.name_en || jurisdiction?.id || '',
      destination_country: jurisdiction?.id || '',
    },
    eligibility,
    required_documents: requiredDocuments,
    required_documents_manifest: proc?.required_documents_manifest || [],
    rights_and_limits: rightsAndLimits,
    outcome,
    process: { steps, showAllPhases: true },
    filings,
    forms: [],
    documents: [],
    explainers,
  };
}

/** Map collection_profile control → intake data_type. */
function controlToDataType(control, concept) {
  const c = String(control || '').toLowerCase();
  if (concept === 'person.nationality' || c === 'country_select') return 'country';
  if (concept === 'person.email' || c === 'email') return 'email';
  if (concept === 'person.phone' || c === 'tel') return 'tel';
  if (c === 'date') return 'date';
  if (c === 'radio') return 'enum';
  return 'text';
}

/** Requirement descriptions matching these keywords get a short canonical question label. */
function canonicalRequirementLabel(description) {
  const lower = String(description || '').toLowerCase();
  if (lower.includes('quota') || lower.includes('decreto') || lower.includes('flussi') || lower.includes('flows-decree')) {
    return 'Quota / flows-decree slot or exemption';
  }
  return null;
}

function infoItem({ id, group, label, dataType = 'text', required = 'required' }) {
  return {
    id,
    kind: 'info',
    display_group: group,
    prompt: { text: { en: label } },
    data_type: dataType,
    required,
    collect: true,
    source: 'applicant',
  };
}

/**
 * Applicant identity/contact fields from gm-content ``collection_profile.baseline``.
 * This is the single source for the Personal details / "Applicant" group.
 */
export function buildApplicantItemsFromCollectionProfile(content) {
  const baseline = content?.collection_profile?.baseline || [];
  return baseline.map((row) => {
    const concept = String(row?.concept || '').trim();
    const label = pickEn(row?.label) || concept;
    const optional = concept === 'person.surname_2';
    return infoItem({
      id: concept,
      group: 'applicant',
      label,
      dataType: controlToDataType(row?.control, concept),
      required: optional ? 'optional' : 'required',
    });
  }).filter((item) => item.id);
}

/**
 * Builds an intake spec (info + document items) from a raw jurisdiction procedure entry,
 * for procedures that don't yet have a curated ``content.intake[slug]`` spec authored.
 * Applicant group = collection_profile.baseline from JSON.
 * Background group = dynamic extras from procedure requirements.
 */
function buildJurisdictionIntakeSpec(proc, content) {
  const items = buildApplicantItemsFromCollectionProfile(content);

  (proc?.requirements || []).forEach((r) => {
    if (r.pattern_ref === 'identity') return; // covered by Applicant/passport fields
    const description = rawText(r.description);
    if (!description) return;
    const canonical = canonicalRequirementLabel(description);
    const label = canonical || `Evidence / information: ${description}`;
    items.push(infoItem({
      id: `background.${String(r.requirement_id || '').split(':').pop() || items.length}`,
      group: 'background',
      label,
      required: r.applicability === 'optional' ? 'optional' : 'required',
    }));
  });

  (proc?.required_documents_manifest || []).forEach((d, i) => {
    const text = rawText(d.note);
    if (!text) return;
    const requirement = String(d.requirement || '').trim();
    const lower = text.toLowerCase();
    let docId;
    if (requirement === 'identity' || /passport/.test(lower)) docId = 'doc.passport';
    else if (requirement === 'category' || /contract|engagement/.test(lower)) docId = 'doc.contract';
    else if (/visa/.test(lower)) docId = 'doc.visa';
    else docId = `doc.${String(requirement || `document_${i}`).replace(/[^a-z0-9]+/gi, '_').toLowerCase()}`;
    const titleMap = {
      category: 'Engagement contract',
      qualification: 'Professional qualification certificate',
      accommodation: 'Proof of accommodation',
      identity: 'Passport',
      health_insurance: 'Health insurance certificate',
      means: 'Proof of means (CUD / tax declaration)',
    };
    const shortTitle = titleMap[requirement] || text;
    items.push({
      id: docId,
      kind: 'document',
      display_group: 'documents',
      short_title: shortTitle,
      prompt: { text: { en: shortTitle } },
      note: text,
      required: d.mandatory === false ? 'optional' : 'required',
      collect: true,
      source: 'applicant',
    });
  });

  return {
    items,
    display_groups: {
      applicant: { text: { en: 'Applicant' } },
      background: { text: { en: 'Background and declarations' } },
      documents: { text: { en: 'Documents to provide' } },
    },
  };
}

export async function getProcedureBundle(slug) {
  const content = await loadGmContent();
  const card = content?.procedures?.[slug];
  if (!card) {
    const match = findJurisdictionProcedure(content, slug);
    if (!match) return null;
    const { jurisdiction, proc } = match;
    return {
      slug,
      card: buildJurisdictionCard(jurisdiction, proc),
      intake: content?.intake?.[slug]
        || proc?.intake
        || buildJurisdictionIntakeSpec(proc, content),
    };
  }
  return {
    slug,
    card,
    intake: content?.intake?.[slug] || buildJurisdictionIntakeSpec(
      { requirements: [], required_documents_manifest: [] },
      content,
    ),
  };
}

export function isProcedureBuilt(card) {
  if (!card) return false;
  const built = card.built;
  return built === true || built === 'true';
}

/** Map gm-content canonical_id → built procedure slug (when a card exists). */
export function buildCanonicalToSlugMap(data) {
  const map = {};
  Object.entries(data?.procedures || {}).forEach(([slug, card]) => {
    const cid = card?.procedure?.canonical_id;
    if (cid) map[cid] = slug;
  });
  return map;
}

function pickProcedureTitle(proc) {
  if (!proc) return '';
  const label = proc.label;
  if (label && typeof label === 'object') {
    const fromText = pickEn(label.text || label);
    if (fromText) return fromText;
  }
  const procMeta = proc.procedure || proc;
  return pickEn(procMeta?.title) || pickEn(procMeta?.label) || proc.canonical_id || '';
}

function pickProcedureSubtitle(proc) {
  const procMeta = proc?.procedure || proc;
  return pickEn(procMeta?.subtitle) || pickEn(proc?.description) || '';
}

function resolveProcedureEntry(canonicalId, slug, proc, builtSlugs) {
  const title = pickProcedureTitle(proc) || canonicalId || slug;
  const routeSlug = builtSlugs.has(slug) ? slug : (builtSlugs.has(canonicalId) ? canonicalId : slug);
  return {
    slug: routeSlug,
    canonicalId: canonicalId || null,
    title,
    subtitle: pickProcedureSubtitle(proc),
    // Jurisdiction catalog entries are selectable programs (no "coming soon" gating).
    built: true,
  };
}

/**
 * Build country + procedure lists from gm-kb-store.json.
 * Prefers ``jurisdictions`` (full KB), then ``catalog.tiers``, then built ``procedures``.
 */
export function buildProcedureCatalog(data) {
  const canonicalToSlug = buildCanonicalToSlugMap(data);
  const builtSlugs = new Set(Object.keys(data?.procedures || {}));

  if (Array.isArray(data?.jurisdictions) && data.jurisdictions.length > 0) {
    const countries = [];
    const proceduresByDest = {};

    data.jurisdictions.forEach((jurisdiction) => {
      const code = String(jurisdiction.id || '').toLowerCase();
      if (!code) return;
      const name = jurisdiction.name_en || code.toUpperCase();
      const rows = (jurisdiction.procedures || [])
        .map((proc) => {
          const canonicalId = proc.canonical_id || proc.cid || null;
          const slug = (canonicalId && canonicalToSlug[canonicalId]) || canonicalId;
          if (!slug) return null;
          return resolveProcedureEntry(canonicalId, slug, proc, builtSlugs);
        })
        .filter(Boolean)
        .sort((a, b) => a.title.localeCompare(b.title));

      if (!rows.length) return;
      countries.push({ code, name, count: rows.length });
      proceduresByDest[code] = rows;
    });

    countries.sort((a, b) => a.name.localeCompare(b.name));
    return { countries, proceduresByDest };
  }

  if (Array.isArray(data?.catalog?.tiers) && data.catalog.tiers.length > 0) {
    const byCountryName = {};
    data.catalog.tiers.forEach((entry) => {
      const countryName = entry.country || '';
      const canonicalId = entry.cid || entry.canonical_id;
      if (!countryName || !canonicalId) return;
      const slug = canonicalToSlug[canonicalId] || canonicalId;
      if (!byCountryName[countryName]) byCountryName[countryName] = [];
      byCountryName[countryName].push(
        resolveProcedureEntry(canonicalId, slug, { canonical_id: canonicalId, label: { text: { en: canonicalId } } }, builtSlugs)
      );
    });

    const countries = [];
    const proceduresByDest = {};
    Object.entries(byCountryName).forEach(([countryName, rows]) => {
      const code = rows[0]?.slug?.split?.('-')?.[0]?.toLowerCase()
        || String(countryName).slice(0, 2).toLowerCase();
      const sorted = [...rows].sort((a, b) => a.title.localeCompare(b.title));
      countries.push({ code, name: countryName, count: sorted.length });
      proceduresByDest[code] = sorted;
    });
    countries.sort((a, b) => a.name.localeCompare(b.name));
    return { countries, proceduresByDest };
  }

  const proceduresByDest = {};
  Object.entries(data?.procedures || {}).forEach(([slug, card]) => {
    const route = SLUG_TO_ROUTE[slug];
    if (!route) return;
    const code = route.split('/')[0];
    if (!proceduresByDest[code]) proceduresByDest[code] = [];
    proceduresByDest[code].push(
      resolveProcedureEntry(card?.procedure?.canonical_id, slug, card, builtSlugs)
    );
  });
  Object.values(proceduresByDest).forEach((arr) => arr.sort((a, b) => a.title.localeCompare(b.title)));

  const countries = Object.entries(proceduresByDest)
    .map(([code, rows]) => ({
      code,
      name: code.toUpperCase(),
      count: rows.length,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  return { countries, proceduresByDest };
}

/** Resolve routing slug from stored org/customer procedure id (slug or canonical_id). */
export function resolveRouteProcedureSlug(storedSlug, data) {
  if (!storedSlug) return '';
  const clean = String(storedSlug).trim();
  if (data?.procedures?.[clean]) return clean;
  const map = buildCanonicalToSlugMap(data || {});
  return map[clean] || clean;
}
